import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { of } from 'rxjs/observable/of';
import { IPlugin } from '../../../models/plugin';
import { PluginsService } from '../plugins.service';
import { PreviewService } from '../../preview/preview.service';
import { contains, groupBy, find, propEq } from 'ramda/es';
import { SessionsService } from '../../sessions/sessions.service';
import { ISession } from '../../../models/session';
import { ILink } from '../../../models/link';
import { getSetupCosts, getAmountToCredit } from '../../shared/live-mode/live-mode.component';

const pluginTypeOrder = {
  teasing: 0,
  live: 1,
  closing: 2
};

@Component({
  selector: 'plugins-list',
  templateUrl: './plugins.component.html',
  styleUrls: ['../plugins.scss']
})
export class PluginListComponent implements OnInit, OnDestroy {
  @Input('session') session: ISession;

  public previewHash;
  public tabs = [];
  public links: ILink[] = [];
  public plugins: IPlugin[] = [];
  public currentTab: any;
  public amountToCredit: number = getSetupCosts(1);
  public multiplicator: number = 1;

  constructor(
    private pluginsService: PluginsService,
    private previewService: PreviewService,
    public sessionsService: SessionsService
  ) {}

  ngOnDestroy() {}

  ngOnInit() {
    this.pluginsService.getAll(this.session).subscribe(plugins => {
      this.plugins = plugins;
      this.tabs = [
        // Webevent:
        {
          name: 'features.webevent',
          type: 'video',
          models: ['mixte', 'video', 'vod'],
          categories: ['teasing', 'live', 'closing']
        },
        // webapp
        {
          name: 'features.webapp',
          type: 'interactive',
          models: ['mixte', 'interactive'],
          categories: ['live']
        }
      ]
        .filter(tab => contains(this.session.types.sessionType, tab.models))
        .map(tab => {
          return Object.assign(tab, {
            plugins: groupBy(
              plugin => plugin.category,
              plugins.filter(plugin => {
                return (
                  contains(tab.type, plugin.sessionTypes) &&
                  contains(plugin.category, tab.categories)
                );
              })
            )
          });
        });
      this.onTabChange(this.tabs[0]);
      this.updateAmountToCredit();
    });
  }

  togglePlugin(plugin: IPlugin, state: boolean) {
    if (this.session.types.sessionType === 'vod' && plugin.name !== 'uploaded_files') {
      return;
    }
    // block plugin activation if the session is live
    if (this.sessionsService.isPremium$.value) {
      return;
    }
    return this.pluginsService
      .togglePlugin(plugin, this.session, state)
      .mergeMap(status => {
        if (status.pluginName === 'poll' || status.pluginName === 'vote_webapp') {
          let secondPlugin = find(
            propEq('name', status.pluginName === 'poll' ? 'vote_webapp' : 'poll')
          )(this.plugins);
          return this.pluginsService.togglePlugin(secondPlugin, this.session, state).do(status => {
            this.plugins.forEach(plugin => {
              if (plugin.name === secondPlugin.name) {
                plugin.isActivated = status.isActivated;
              }
            });
          });
        }
        return of(status);
      })
      .subscribe(status => {
        plugin.isActivated = status.isActivated;
        this.updateAmountToCredit();
        this.previewHash = new Date().getTime();
        // Trigger tab change to get new links list
        this.onTabChange();
        this.sessionsService.setComplete(this.session, 'features');
      });
  }

  onTabChange(tab?: any) {
    if (tab) {
      this.currentTab = tab;
    }
    const type = this.currentTab.type === 'video' ? 'webevent' : 'webapp';
    this.links = this.previewService.getLinks(type, this.session, this.plugins);
  }

  updateAmountToCredit() {
    this.amountToCredit = getAmountToCredit(
      this.session.types,
      this.plugins,
      this.multiplicator
    );
  }

  onMultiplicatorChange(multiplicator: number) {
    this.multiplicator = multiplicator;
    this.updateAmountToCredit();
  }

  pluginDisabled(plugin: IPlugin): boolean {
    return this.sessionsService.isPremium$.value || (this.session.types.sessionType === 'vod' && plugin.name !== 'uploaded_files');
  }

  pluginClickable(plugin: IPlugin): boolean {
    return !(this.sessionsService.isPremium$.value || (this.session.types.sessionType === 'vod' && plugin.name !== 'uploaded_files'));
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/plugins/list/plugins.component.ts